import React from 'react';
import { ReactComponent as ChildIcon } from '@assets/images/app/icon-child.svg';
import { ReactComponent as ClockIcon } from '@assets/images/app/icon-clock.svg';
import { ReactComponent as LocationIcon } from '@assets/images/app/icon-location.svg';
import { ReactComponent as TruckIcon } from '@assets/images/app/icon-truck.svg';
import { ReactComponent as UsersIcon } from '@assets/images/app/icon-users.svg';
import { PRODUCT_ACTIVITY_TYPE_TEXT, ProductActivityType, ProductModel } from '@models/product';
import { getAddressesText } from '@utils/dataTransform';
import { Divider } from './Divider';

export const ProductInformation: React.FC<{ data: ProductModel }> = (props) => {
  const data = props.data;

  const activityType = data?.activityType || ProductActivityType.EXPERIENTIAL;
  const ActivityTypeIcon = PRODUCT_ACTIVITY_TYPE_TEXT[activityType]?.Icon;
  const displayStudentsSize = (data?.studentsMin ?? false) && (data?.studentsMax ?? false);
  const tags = [...(data?.labels?.map((label) => label.name) || []), ...(data?.tags || [])];
  return (
    <div id="product-information-preview" style={{ padding: '24px 20px', minWidth: '300px', width: '100%' }}>
      <div
        style={{
          color: 'var(--Neutral-100, #242424)',
          fontFamily: 'Pretendard',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '22px',
          marginBottom: '12px',
        }}>
        기본정보
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          color: 'var(--Neutral-90, #424242)',
          fontFamily: 'Pretendard',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '22px',
        }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: '24px' }}>
          <ActivityTypeIcon stroke={'#808387'} style={{ width: '24px', height: '24px', flexShrink: 0 }} />
          <div style={{ marginLeft: '12px', whiteSpace: 'nowrap', flex: 1 }}>
            {activityType && <div>{PRODUCT_ACTIVITY_TYPE_TEXT[activityType]?.title}</div>}
          </div>
        </div>
        {activityType === ProductActivityType.DELIVERY && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: '24px' }}>
              <TruckIcon stroke={'#808387'} style={{ width: '24px', height: '24px', flexShrink: 0 }} />
              <div style={{ marginLeft: '12px', whiteSpace: 'nowrap', flex: 1 }}>
                택배비
                {data?.shippingFee === 0 ? ` 무료` : ` ${(data?.shippingFee || 0).toLocaleString()}원`}
              </div>
            </div>
            {data?.sellerShortComment && (
              <div style={{ marginLeft: '36px' }}>
                <div
                  style={{
                    color: '#808387',
                    fontSize: '14px',
                    lineHeight: '21px',
                    marginTop: '4px',
                    whiteSpace: 'pre-wrap',
                  }}>
                  {data?.sellerShortComment}
                </div>
              </div>
            )}
          </div>
        )}
        {(data?.durations || []).filter((duration) => duration && duration.duration).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: '24px' }}>
            <ClockIcon stroke={'#808387'} style={{ width: '24px', height: '24px', flexShrink: 0 }} />
            <div style={{ marginLeft: '12px', whiteSpace: 'nowrap', flex: 1 }}>
              <div>
                {(data?.durations || [])
                  .map((duration) => {
                    return `${duration?.duration}분`;
                  })
                  .join(', ')}
              </div>
            </div>
          </div>
        )}
        {!data?.durations && data?.classDuration && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: '24px' }}>
            <ClockIcon stroke={'#808387'} style={{ width: '24px', height: '24px', flexShrink: 0 }} />
            <div style={{ marginLeft: '12px', whiteSpace: 'nowrap', flex: 1 }}>{data?.classDuration}분</div>
          </div>
        )}
        {data?.ageMin > 0 && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: '24px' }}>
            <ChildIcon stroke={'#808387'} style={{ width: '24px', height: '24px', flexShrink: 0 }} />
            <div style={{ marginLeft: '12px', whiteSpace: 'nowrap', flex: 1 }}>
              <div>{`${data?.ageMin}세 ~ ${data?.ageMax > 13 ? '14세+' : `${data?.ageMax}세`}`}</div>
            </div>
          </div>
        )}
        {displayStudentsSize && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: '24px' }}>
            <UsersIcon stroke={'#808387'} style={{ width: '24px', height: '24px', flexShrink: 0 }} />
            <div style={{ marginLeft: '12px', whiteSpace: 'nowrap', flex: 1 }}>
              <div>
                {data?.studentsMin === -1
                  ? '인원 제한없음'
                  : `${data?.studentsMin}인 ~ ${data?.studentsMax === -1 ? '인원 제한없음' : `${data?.studentsMax}인`}`}
              </div>
            </div>
          </div>
        )}
        {[
          ProductActivityType.STUDIO,
          ProductActivityType.EXPERIENTIAL,
          ProductActivityType.HOME,
          ProductActivityType.TICKET,
        ].includes(activityType as ProductActivityType) &&
          data?.location &&
          getAddressesText({ availableArea: data?.location, activityType }) !== '-' && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '4px',
                width: '100%',
                alignItems: 'flex-start',
                minHeight: '24px',
              }}>
              <LocationIcon stroke={'#808387'} style={{ width: '24px', height: '24px', flexShrink: 0 }} />
              <div style={{ marginLeft: '12px', whiteSpace: 'nowrap', flex: 1 }}>
                <div>{getAddressesText({ availableArea: data?.location, activityType })}</div>
              </div>
            </div>
          )}
      </div>
      {data.sellerComment && (
        <div style={{ marginTop: '36px' }}>
          <div
            style={{
              color: 'var(--Neutral-100, #242424)',
              fontFamily: 'Pretendard',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '22px',
              marginBottom: '12px',
            }}>
            구매안내
          </div>
          <div
            style={{
              color: 'var(--Neutral-90, #424242)',
              fontFamily: 'Pretendard',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '22px',
              whiteSpace: 'pre-wrap',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
            {data?.sellerComment.split('\n').map((comment) => {
              return <div key={comment}>{comment}</div>;
            })}
          </div>
        </div>
      )}
      {tags.length > 0 && (
        <>
          <Divider style={{ margin: '20px 0', padding: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '20px' }}>
            {tags.slice(0, 2).map((item, index) => {
              return (
                <div
                  key={`tag-${index}`}
                  style={{
                    display: 'flex',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    borderRadius: '6px',
                    backgroundColor: '#eff1f4',
                    height: '28px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '8px',
                  }}
                  onClick={() => {
                    // this.onSearchDone(item);
                  }}>
                  <div
                    style={{
                      color: 'var(--Neutral-70, #808387)',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      wordBreak: 'keep-all',
                      whiteSpace: 'nowrap',
                    }}>
                    {item}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
